import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
	WbGrid,
	WbGridCol,
	WbGridRow,
	WbSelect,
	WbText,
	WbTooltip,
	WbValueSlider,
} from '@workbench/react';
import { useTranslation } from 'react-i18next';

import RDivider from '../../Common/RDivider/RDivider';
import VimdtButton from '../Common/VimdtButton';
import VimdtInput from '../Common/VimdtInput';
import VimdtSwitch from '../Common/VimdtSwitch';
import VimdtAccordion from '../Common/VimdtAccordion';
import PanelHeader from '../Common/PanelHeader';
import ImageInput from '../Common/ImageInput';
import mergeImages from '../../../Utils/Vimdt/mergeImages';
import saveJson from '../../../Utils/Vimdt/saveJson';
import validateComposeSettings from '../../../Utils/Vimdt/validateComposeSettings';
import _notification from '../../../Utils/Vimdt/notification';
import useDebouncedEffect from '../../../Utils/useDebouncedEffect';
import {
	getStorageItem,
	removeStorageItem,
	setStorageItem,
	STORAGE_KEYS,
} from '../../../Utils/Vimdt/storage';
import './ImageCompose.scss';

const MIME_TYPES = { jpeg: 'image/jpeg', webp: 'image/webp' };

const dataURItoBlob = (dataURI, mimeType = 'image/png') => {
	const byteString = window.atob(dataURI.split(',')[1]);
	const buffer = new ArrayBuffer(byteString.length);
	const view = new Uint8Array(buffer);
	for (let i = 0; i < byteString.length; i++) {
		view[i] = byteString.charCodeAt(i);
	}
	return new Blob([buffer], { type: mimeType });
};

const degreeToTitle = (value) => `EXT${String(value).padStart(3, '0')}`;

function ImageCompose({
	images,
	saveFileName,
	imageType,
	cropped,
	loadedBackground,
	loadedFileFormat,
	sample = false,
}) {
	const { t } = useTranslation();

	const [compositionItems, setCompositionItems] = useState({
		background: sample ? null : getStorageItem(STORAGE_KEYS.BACKGROUND),
		banner: getStorageItem(STORAGE_KEYS.BANNER),
		logo1: getStorageItem(STORAGE_KEYS.LOGO1),
		logo2: getStorageItem(STORAGE_KEYS.LOGO2),
	});

	const [panels, setPanels] = useState([]);
	const [composedImages, setComposedImages] = useState({});
	const [sliderValue, setSliderValue] = useState(0);
	const [activeImage, setActiveImage] = useState(null);
	const [saving, setSaving] = useState(false);
	const [composing, setComposing] = useState(false);
	const [currentVin, setCurrentVin] = useState(Object.keys(images)[0]);
	const [firstTopPx, setFirstTopPx] = useState(20);
	const [firstLeftPx, setFirstLeftPx] = useState(20);
	const [secondTopPx, setSecondTopPx] = useState(20);
	const [secondRightPx, setSecondRightPx] = useState(20);
	const [logoOneSize, setLogoOneSize] = useState([0, 0]);
	const [logoTwoSize, setLogoTwoSize] = useState([0, 0]);
	const [resizePercentLogo1, setResizePercentLogo1] = useState(100);
	const [resizePercentLogo2, setResizePercentLogo2] = useState(100);
	const [scaleToFit, setScaleToFit] = useState(false);

	const fileInputRef = useRef(null);
	const notifyKey = useRef();
	const prevComposedImages = useRef({});

	const IMAGE_SIZE = cropped ? [1600, 1200] : [1920, 1080];
	const mimeType = MIME_TYPES[imageType] || 'image/png';

	const calculateStepSize = () => {
		const exteriorImagesCount = (images[currentVin] || []).filter((data) =>
			data.title.startsWith('EXT')
		).length;
		return exteriorImagesCount > 1 ? 350 / (exteriorImagesCount - 1) : 0;
	};

	const [sliderStepSize, setSliderStepSize] = useState(calculateStepSize());

	const getDegreeFromIndex = (index) => parseInt(images[currentVin][index].title.slice(-3), 10);

	const [currentDegree, setCurrentDegree] = useState(getDegreeFromIndex(0));

	useEffect(() => {
		prevComposedImages.current = composedImages;
	}, [composedImages]);

	const openNotification = (title, message) => {
		notifyKey.current = _notification({
			title,
			message,
			duration: 5,
			key: notifyKey.current,
			onClose: () => {
				notifyKey.current = undefined;
			},
		});
	};

	const getResizePercent = (resizePercent) =>
		!resizePercent || resizePercent > 400 || resizePercent < 25 ? 100 : resizePercent;

	const prepareImageForCompose = (image) => {
		let composingImages = [];

		const tmpResizePercentLogo1 = getResizePercent(resizePercentLogo1);
		const tmpResizePercentLogo2 = getResizePercent(resizePercentLogo2);

		if (compositionItems.background != null && !loadedBackground && !sample) {
			const background = { src: compositionItems.background.url, x: 0, y: 0 };
			if (!scaleToFit) {
				background.width = IMAGE_SIZE[0];
				background.height = IMAGE_SIZE[1];
			}
			composingImages.push(background);
		}

		if (compositionItems.banner != null) {
			const banner = {
				src: compositionItems.banner.url,
				x: 0,
				y: IMAGE_SIZE[1] - compositionItems.banner.height,
			};
			if (!scaleToFit) {
				banner.width = IMAGE_SIZE[0];
				banner.height = compositionItems.banner.height;
			}
			composingImages.push(banner);
		}

		if (compositionItems.logo1 != null) {
			composingImages.push({
				src: compositionItems.logo1.url,
				x: firstLeftPx,
				y: firstTopPx,
				width: compositionItems.logo1.width * tmpResizePercentLogo1 * 0.01,
				height: compositionItems.logo1.height * tmpResizePercentLogo1 * 0.01,
			});
		}

		if (compositionItems.logo2 != null) {
			const width = compositionItems.logo2.width * tmpResizePercentLogo2 * 0.01;
			composingImages.push({
				src: compositionItems.logo2.url,
				x: IMAGE_SIZE[0] - secondRightPx - width,
				y: secondTopPx,
				width,
				height: compositionItems.logo2.height * tmpResizePercentLogo2 * 0.01,
			});
		}

		if (image.title.startsWith('EXT')) {
			// A pre-rendered background means the vehicle has to go behind the overlays.
			const shouldUnshift = loadedBackground || loadedFileFormat === 'jpeg' || sample;
			composingImages[shouldUnshift ? 'unshift' : 'push']({ src: image.url, x: 0, y: 0 });
		} else {
			composingImages = [{ src: image.url, x: 0, y: 0 }];
		}

		return composingImages;
	};

	const previewComposedImage = async (value = sliderValue) => {
		if (!sliderStepSize) return;
		const index = value / sliderStepSize;
		const image = images[currentVin][index];
		if (!image) return;

		const preparedImages = prepareImageForCompose(image);
		const tmpComposedImages = {
			[currentVin]: images[currentVin].map((item) => ({
				title: item.title,
				blob: item.blob,
				b64: item.url,
			})),
		};

		const composedImageURI = await mergeImages(preparedImages, {
			width: IMAGE_SIZE[0],
			height: IMAGE_SIZE[1],
		});
		const target = tmpComposedImages[currentVin].findIndex((item) => item.title === image.title);
		tmpComposedImages[currentVin][target] = {
			title: image.title,
			blob: dataURItoBlob(composedImageURI),
			b64: composedImageURI,
		};

		setComposedImages(tmpComposedImages);
		setActiveImage(tmpComposedImages[currentVin][index].b64);
		setSliderStepSize(calculateStepSize());
	};

	useEffect(() => {
		previewComposedImage(sliderValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [compositionItems, currentVin, resizePercentLogo1, resizePercentLogo2, scaleToFit]);

	const validateLogoPosition = () => {
		const title = t('vimdt.imagesTab.imageCompose.rangeErrorTitle');
		const outOfRange = (value, size, limit) =>
			(value > 100 && size < limit) || value < 0 || Number.isNaN(Number(value));

		if (outOfRange(firstTopPx, logoOneSize[1], IMAGE_SIZE[1])) {
			setFirstTopPx(20);
			openNotification(title, t('vimdt.imagesTab.imageCompose.notification.topFirst'));
		}
		if (outOfRange(secondTopPx, logoTwoSize[1], IMAGE_SIZE[1])) {
			setSecondTopPx(20);
			openNotification(title, t('vimdt.imagesTab.imageCompose.notification.topSecond'));
		}
		if (outOfRange(firstLeftPx, logoOneSize[0], IMAGE_SIZE[0])) {
			setFirstLeftPx(20);
			openNotification(title, t('vimdt.imagesTab.imageCompose.notification.leftFirst'));
		}
		if (outOfRange(secondRightPx, logoTwoSize[0], IMAGE_SIZE[0])) {
			setSecondRightPx(20);
			openNotification(title, t('vimdt.imagesTab.imageCompose.notification.rightSecond'));
		}
	};

	useEffect(() => {
		validateLogoPosition();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [firstLeftPx, firstTopPx, secondRightPx, secondTopPx]);

	useDebouncedEffect(
		() => {
			previewComposedImage(sliderValue);
		},
		[firstLeftPx, firstTopPx, secondRightPx, secondTopPx],
		500
	);

	const composeImages = async () => {
		setComposing(true);
		const tmpComposedImages = { [currentVin]: [] };

		for (const image of images[currentVin]) {
			const preparedImages = prepareImageForCompose(image);
			// eslint-disable-next-line no-await-in-loop
			const composedImageURI = await mergeImages(preparedImages, {
				width: IMAGE_SIZE[0],
				height: IMAGE_SIZE[1],
				format: mimeType,
			});
			tmpComposedImages[currentVin].push({
				title: image.title,
				blob: dataURItoBlob(composedImageURI, mimeType),
				b64: composedImageURI,
			});
		}

		setActiveImage(tmpComposedImages[currentVin][sliderValue / sliderStepSize]?.b64);
		setComposing(false);
		setSliderStepSize(calculateStepSize());
		return tmpComposedImages;
	};

	const sliderOnChange = (value) => {
		const degree = getDegreeFromIndex(value / sliderStepSize);
		setSliderValue(value);
		const perspective = prevComposedImages.current[currentVin]?.find(
			(image) => image.title === degreeToTitle(degree)
		);
		setActiveImage(perspective?.b64 || '');
		setCurrentDegree(degree);
	};

	const onImageSet = (details) => {
		setCompositionItems((prev) => ({ ...prev, [details.name]: details }));
		setStorageItem(details.name, details);
		if (details.name === STORAGE_KEYS.LOGO1) setLogoOneSize([details.width, details.height]);
		if (details.name === STORAGE_KEYS.LOGO2) setLogoTwoSize([details.width, details.height]);
	};

	const onImageDelete = (name) => {
		setCompositionItems((prev) => ({ ...prev, [name]: null }));
		removeStorageItem(name);
	};

	const saveComposedImages = async () => {
		setSaving(true);
		const composed = await composeImages();
		const zip = new JSZip();
		const imageFolder = zip.folder(saveFileName);

		Object.keys(composed).forEach((vin) => {
			composed[vin].forEach((image) => {
				const blob = new Blob([image.blob], { type: mimeType });
				imageFolder.file(`${vin}/${image.title}.${imageType}`, blob, { createFolders: true });
			});
		});

		zip
			.generateAsync({ type: 'blob' })
			.then((content) => {
				saveAs(content, saveFileName);
				setSaving(false);
			})
			.catch((err) => {
				console.error(err);
				setSaving(false);
			});
	};

	const handleLoadContent = (event) => {
		const file = event.target.files[0];
		if (!file) {
			event.target.value = null;
			return;
		}

		const fileReader = new FileReader();
		fileReader.readAsText(file, 'UTF-8');
		fileReader.onload = (e) => {
			try {
				const resultJson = JSON.parse(e.target?.result);

				if (!validateComposeSettings(resultJson)) {
					_notification({
						title: t('vimdt.notification.info'),
						message: t('vimdt.notification.error'),
						duration: 10,
						error: true,
					});
					return;
				}

				_notification({
					title: t('vimdt.notification.info'),
					message: t('vimdt.notification.fullyLoad'),
					duration: 10,
				});

				const nullItems = Object.fromEntries(
					Object.keys(compositionItems).map((key) => [key, null])
				);

				Object.entries(resultJson).forEach(([key, value]) => {
					setStorageItem(key, value);
					if (key === STORAGE_KEYS.LOGO1) {
						setFirstLeftPx(value.left);
						setFirstTopPx(value.top);
						setResizePercentLogo1(value.percentage);
					}
					if (key === STORAGE_KEYS.LOGO2) {
						setSecondRightPx(value.right);
						setSecondTopPx(value.top);
						setResizePercentLogo2(value.percentage);
					}
				});

				setCompositionItems({ ...nullItems, ...resultJson });
			} catch (error) {
				_notification({
					title: 'Error',
					message: `${t('vimdt.notification.error')} ${error}`,
					duration: 10,
					error: true,
				});
			}
		};

		event.target.value = null;
	};

	const handleSaveSettings = () => {
		const composeSettings = {};

		if (compositionItems.logo1) {
			composeSettings.logo1 = {
				...compositionItems.logo1,
				top: firstTopPx || '0',
				left: firstLeftPx || '0',
				percentage: resizePercentLogo1 || 100,
			};
		}
		if (compositionItems.logo2) {
			composeSettings.logo2 = {
				...compositionItems.logo2,
				top: secondTopPx || '0',
				right: secondRightPx || '0',
				percentage: resizePercentLogo2 || 100,
			};
		}
		if (compositionItems.banner) composeSettings.banner = compositionItems.banner;
		if (compositionItems.background) composeSettings.background = compositionItems.background;

		if (Object.keys(composeSettings).length === 0) {
			_notification({
				title: t('vimdt.notification.information'),
				message: t('vimdt.notification.nothingToSave'),
				duration: 10,
			});
			return;
		}

		saveJson(composeSettings, 'mb-compose-settings');
	};

	const handleResetSettings = () => {
		setResizePercentLogo1(100);
		setResizePercentLogo2(100);
		setFirstLeftPx(20);
		setFirstTopPx(20);
		setSecondRightPx(20);
		setSecondTopPx(20);
		setCompositionItems(
			Object.fromEntries(Object.keys(compositionItems).map((key) => [key, null]))
		);
		[STORAGE_KEYS.BACKGROUND, STORAGE_KEYS.BANNER, STORAGE_KEYS.LOGO1, STORAGE_KEYS.LOGO2].forEach(
			removeStorageItem
		);
	};

	const hasAnyComposition = Boolean(
		compositionItems.background ||
			compositionItems.banner ||
			compositionItems.logo1 ||
			compositionItems.logo2
	);

	const editVehicleImagesDataTable = [
		{
			key: 1,
			type: t('vimdt.imagesTab.imageCompose.backgroundImage'),
			desc: t('vimdt.imagesTab.imageCompose.editVehicleImagesDataTable.backgroundImageDesc'),
		},
		{
			key: 2,
			type: t('vimdt.imagesTab.imageCompose.bannerImage'),
			desc: t('vimdt.imagesTab.imageCompose.editVehicleImagesDataTable.bannerImageDesc'),
		},
		{
			key: 3,
			type: t('vimdt.imagesTab.imageCompose.logoUpperLeft'),
			desc: t('vimdt.imagesTab.imageCompose.editVehicleImagesDataTable.logoUpperLeftDesc'),
		},
		{
			key: 4,
			type: t('vimdt.imagesTab.imageCompose.logoUpperRight'),
			desc: t('vimdt.imagesTab.imageCompose.editVehicleImagesDataTable.logoUpperRightDesc'),
		},
	];

	const uploadSlot = (label, name, disabled, extras) => (
		<WbGridCol mq1={12} mq5={6}>
			<WbText size="s" className="vimdt-compose__label">
				{label}
			</WbText>
			<ImageInput
				oldImageUrl={compositionItems[name]?.url}
				disabled={disabled}
				onImageSet={onImageSet}
				onImageDelete={onImageDelete}
				name={name}
				fileSize={10}
			/>
			{extras}
		</WbGridCol>
	);

	const logoPositionInput = (id, label, tooltip, getValue, value, name) => (
		<WbTooltip position="top">
			<div className="vimdt-compose__position">
				<VimdtInput
					setId={id}
					setType="number"
					label={label}
					getValue={getValue}
					disabled={compositionItems[name] == null}
					val={value}
				/>
			</div>
			<span slot="content">{tooltip}</span>
		</WbTooltip>
	);

	const selectableVins = Object.keys(images).filter((vin) =>
		images[vin].some((img) => 'title' in img && img.title.startsWith('EXT'))
	);

	const content = (
		<div className="vimdt-compose">
			<WbGrid fullWidth>
				<WbGridRow>
					{uploadSlot(
						t('vimdt.imagesTab.imageCompose.backgroundImage'),
						'background',
						loadedBackground || loadedFileFormat === 'jpeg' || sample
					)}
					{uploadSlot(t('vimdt.imagesTab.imageCompose.bannerImage'), 'banner', false)}
					{uploadSlot(
						t('vimdt.imagesTab.imageCompose.logoUpperLeft'),
						'logo1',
						false,
						<div className="vimdt-compose__positions">
							{logoPositionInput(
								'1Top',
								t('vimdt.imagesTab.imageCompose.logoTop'),
								t('vimdt.imagesTab.imageCompose.tooltipOnPosition'),
								setFirstTopPx,
								firstTopPx,
								'logo1'
							)}
							{logoPositionInput(
								'1Left',
								t('vimdt.imagesTab.imageCompose.logoLeft'),
								t('vimdt.imagesTab.imageCompose.tooltipOnPosition'),
								setFirstLeftPx,
								firstLeftPx,
								'logo1'
							)}
							{logoPositionInput(
								'LogoLeftResize',
								t('vimdt.imagesTab.imageCompose.logoResize'),
								t('vimdt.imagesTab.imageCompose.tooltipOnResize'),
								setResizePercentLogo1,
								resizePercentLogo1,
								'logo1'
							)}
						</div>
					)}
					{uploadSlot(
						t('vimdt.imagesTab.imageCompose.logoUpperRight'),
						'logo2',
						false,
						<div className="vimdt-compose__positions">
							{logoPositionInput(
								'2Top',
								t('vimdt.imagesTab.imageCompose.logoTop'),
								t('vimdt.imagesTab.imageCompose.tooltipOnPosition'),
								setSecondTopPx,
								secondTopPx,
								'logo2'
							)}
							{logoPositionInput(
								'2Right',
								t('vimdt.imagesTab.imageCompose.logoRight'),
								t('vimdt.imagesTab.imageCompose.tooltipOnPosition'),
								setSecondRightPx,
								secondRightPx,
								'logo2'
							)}
							{logoPositionInput(
								'LogoRightResize',
								t('vimdt.imagesTab.imageCompose.logoResize'),
								t('vimdt.imagesTab.imageCompose.tooltipOnResize'),
								setResizePercentLogo2,
								resizePercentLogo2,
								'logo2'
							)}
						</div>
					)}

					<WbGridCol mq1={12} mq5={6}>
						<VimdtSwitch
							label={t('vimdt.imagesTab.imageCompose.scaleToFit')}
							onBtn={t('vimdt.components.onButton')}
							offBtn={t('vimdt.components.offButton')}
							status={[t('vimdt.components.onButton'), t('vimdt.components.offButton')]}
							getValue={setScaleToFit}
							defaultValue={scaleToFit}
						/>
					</WbGridCol>

					<WbGridCol mq1={12}>
						<div className="vimdt-compose__actions">
							<VimdtButton
								disabled={!hasAnyComposition}
								label={t('vimdt.loadSaveButtons.saveSettings')}
								filledIn={false}
								onClick={handleSaveSettings}
							/>
							<VimdtButton
								label={t('vimdt.loadSaveButtons.loadSettings')}
								filledIn={false}
								onClick={() => fileInputRef.current?.click()}
							/>
							<VimdtButton
								label={t('vimdt.loadSaveButtons.resetSettings')}
								filledIn={false}
								onClick={handleResetSettings}
							/>
							<input
								type="file"
								accept="application/json"
								onChange={handleLoadContent}
								ref={fileInputRef}
								id="uploadSettings"
								hidden
							/>
						</div>
					</WbGridCol>
				</WbGridRow>
			</WbGrid>

			<RDivider />

			<WbGrid fullWidth>
				<WbGridRow>
					<WbGridCol mq1={12} mq3={6}>
						<WbSelect compact>
							<WbText slot="label" size="s">
								{t('vimdt.imagesTab.imageCompose.selectVIN')}
							</WbText>
							<select
								aria-label={t('vimdt.imagesTab.imageCompose.selectVIN')}
								value={currentVin}
								disabled={composing}
								onChange={(event) => setCurrentVin(event.target.value)}
							>
								{selectableVins.map((vin) => (
									<option key={vin} value={vin}>
										{vin}
									</option>
								))}
							</select>
						</WbSelect>
					</WbGridCol>

					<WbGridCol mq1={12} mq3={6}>
						<div className="vimdt-compose__actions">
							<VimdtButton
								disabled={!hasAnyComposition || saving || sample}
								label={t('vimdt.loadSaveButtons.saveComposedImage')}
								onClick={saveComposedImages}
								loading={saving}
								filledIn={false}
							/>
						</div>
					</WbGridCol>

					<WbGridCol mq1={12}>
						<div className="vimdt-compose__preview">
							<WbText size="s">{`${t(
								'vimdt.imagesTab.imageCompose.preview'
							)}: ${currentDegree}°`}</WbText>
							<img className="vimdt-compose__preview-image" src={activeImage} alt="" />
							<WbValueSlider
								min={0}
								max={350}
								step={sliderStepSize}
								value={sliderValue}
								label={t('vimdt.imagesTab.imageCompose.preview')}
								formatTooltipValue={() => `${currentDegree}°`}
								onWbinput={(event) => sliderOnChange(event.detail)}
								onWbchange={(event) => previewComposedImage(event.detail)}
							/>
						</div>
					</WbGridCol>
				</WbGridRow>
			</WbGrid>
		</div>
	);

	return (
		<VimdtAccordion
			activeKeys={panels}
			onChange={setPanels}
			items={[
				{
					key: 'compose',
					header: (
						<PanelHeader
							headerName={t('vimdt.imagesTab.imageCompose.editVehicleImages')}
							data={editVehicleImagesDataTable}
							description={t(
								'vimdt.imagesTab.imageCompose.editVehicleImagesDataTable.dataTableDesc'
							)}
						/>
					),
					content,
				},
			]}
		/>
	);
}

export default ImageCompose;
